import React, { useState } from 'react';
import { Elements, useStripe, useElements, IbanElement } from '@stripe/react-stripe-js';
import { Building2, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { getStripe } from '../../lib/stripe';

const IBAN_STYLE = {
  base: {
    color: '#ffffff',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: '16px',
    fontWeight: '400',
    '::placeholder': { color: '#888' },
    ':-webkit-autofill': { color: '#ffffff' },
  },
  invalid: { color: '#ff6a6a', iconColor: '#ff6a6a' },
};

/**
 * Inner form using Stripe Elements (IbanElement + mandate confirmation).
 * onMandateSigned is called once the SetupIntent succeeds.
 */
const SEPAInnerForm = ({ clientSecret, customerName, customerEmail, firstChargeAmount, signatureImage, onMandateSigned, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !accepted) return;
    setBusy(true);
    setError(null);
    try {
      const iban = elements.getElement(IbanElement);
      const { error: confirmErr, setupIntent } = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: iban,
          billing_details: {
            name: customerName,
            email: customerEmail,
          },
        },
      });
      if (confirmErr) {
        setError(confirmErr.message || 'Erreur lors de la validation du mandat');
        setBusy(false);
        return;
      }
      onMandateSigned(setupIntent);
    } catch (err) {
      setError(err.message || 'Erreur inattendue');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="sepa-form">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-400/5 border border-yellow-400/20">
        <Building2 className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold text-white">Prélèvement SEPA</div>
          <div className="text-xs text-white/70 mt-1">
            Renseignez l'IBAN du compte à débiter. Le mandat sera signé électroniquement.
            Premier prélèvement : <strong className="text-yellow-400">{firstChargeAmount.toFixed(2)} €</strong>
            {' '}(caution + 1er loyer + frais), uniquement après validation de notre équipe.
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
          IBAN
        </label>
        <div className="p-4 rounded-lg bg-white/5 border border-white/15 focus-within:border-yellow-400/60 transition">
          <IbanElement
            options={{
              supportedCountries: ['SEPA'],
              placeholderCountry: 'FR',
              style: IBAN_STYLE,
            }}
          />
        </div>
        <div className="text-[11px] text-white/40 mt-1.5 font-mono">
          Exemple : FR76 1234 5678 9012 3456 7890 123
        </div>
      </div>

      <div data-testid="sepa-signature-block">
        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
          Votre signature (reprise du contrat)
        </label>
        {signatureImage ? (
          <div className="p-3 rounded-lg bg-white border border-white/15 flex items-center justify-center">
            <img src={signatureImage} alt="Votre signature" className="max-h-20 object-contain" data-testid="sepa-signature-image" />
          </div>
        ) : (
          <div className="p-3 rounded-lg bg-white/5 border border-white/15 text-xs text-white/50 italic">
            Signature électronique enregistrée à l'étape précédente.
          </div>
        )}
        <div className="text-[11px] text-white/40 mt-1.5">
          Cette signature, déjà apposée sur votre contrat de location, est appliquée à votre mandat de prélèvement SEPA.
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-white/5 transition" data-testid="sepa-mandate-checkbox-label">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-2 border-white/30 bg-transparent text-yellow-400 focus:ring-yellow-400"
          data-testid="sepa-mandate-checkbox"
        />
        <span className="text-xs text-white/70 leading-relaxed">
          En cochant cette case, vous autorisez <strong className="text-white">Phocea Rent (KF DIFFUSION PHOCEA LIGHT)</strong>
          {' '}et Stripe (notre prestataire de paiement) à envoyer des instructions à votre banque pour
          débiter votre compte conformément à ces instructions. Vous bénéficiez du droit de remboursement
          par votre banque selon les conditions décrites dans la convention que vous avez passée avec elle.
          Une demande de remboursement doit être présentée dans les 8 semaines suivant la date de débit
          de votre compte pour un prélèvement autorisé.
        </span>
      </label>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm" data-testid="sepa-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 font-semibold transition"
          data-testid="sepa-cancel-btn"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || !accepted || busy}
          className="flex-1 py-3 rounded-lg bg-yellow-400 hover:bg-yellow-300 disabled:bg-yellow-400/30 disabled:cursor-not-allowed text-black font-bold transition flex items-center justify-center gap-2"
          data-testid="sepa-submit-btn"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Signature en cours...</> : <><ShieldCheck className="w-4 h-4" /> Signer le mandat</>}
        </button>
      </div>
    </form>
  );
};

/** Outer wrapper that provides Stripe Elements context with the SEPA appearance. */
export const SEPAPaymentForm = (props) => {
  const stripePromise = getStripe();
  const options = {
    clientSecret: props.clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#facc15',
        colorBackground: '#0a0a0a',
        colorText: '#ffffff',
        colorDanger: '#ff6a6a',
        fontFamily: 'Helvetica, Arial, sans-serif',
        borderRadius: '8px',
      },
    },
    locale: 'fr',
  };
  if (!stripePromise) {
    return <div className="text-red-300 p-4">Stripe non configuré. Contactez l'administrateur.</div>;
  }
  return (
    <Elements stripe={stripePromise} options={options}>
      <SEPAInnerForm {...props} />
    </Elements>
  );
};

export default SEPAPaymentForm;
